import React, { useLayoutEffect, useRef, useState } from 'react'

import type { SxProps } from '@mui/material'
import Box from '@mui/material/Box'

interface MiniDescriptionMUIProps {
  content: string
  isLoading: boolean
  sx?: SxProps
}

export const MiniDescriptionMUI: React.FC<MiniDescriptionMUIProps> = ({
  content,
  isLoading,
  sx,
}) => {
  const [visibleLines, setVisibleLines] = useState<number>(0)
  const descriptionRef = useRef<HTMLDivElement>(null)

  const calculateVisibleLines = () => {
    if (descriptionRef.current) {
      const descriptionElement = descriptionRef.current
      const parentElement = descriptionElement.parentElement

      if (parentElement) {
        const parentChildren = Array.from(parentElement.children) as HTMLElement[]

        const nonDescriptionChildrenHeight = parentChildren.reduce((totalHeight, child) => {
          if (child !== descriptionElement) {
            return totalHeight + child.clientHeight
          }
          return totalHeight
        }, 0)

        const parentHeight = parentElement.clientHeight

        const availableHeight = parentHeight - nonDescriptionChildrenHeight - 24 //-24 because the height of padding is not available height for text

        const descriptionLineHeight =
          parseInt(window.getComputedStyle(descriptionElement).lineHeight, 10) + 4 //add 4 pixels because of trailing characters like p

        const calculatedVisibleLines = Math.floor(availableHeight / descriptionLineHeight)

        if (calculatedVisibleLines !== visibleLines) {
          setVisibleLines(calculatedVisibleLines)
        }
      }
    }
  }

  useLayoutEffect(() => {
    calculateVisibleLines()

    const resizeObserver = new ResizeObserver(() => calculateVisibleLines())

    const parentElement = descriptionRef.current?.parentElement
    if (parentElement) resizeObserver.observe(parentElement)

    return () => {
      if (parentElement) resizeObserver.unobserve(parentElement)
    }
  }, [content, isLoading])

  return (
    <Box
      dangerouslySetInnerHTML={{ __html: content }}
      ref={descriptionRef}
      sx={{
        display: '-webkit-box',
        fontSize: 13,
        lineHeight: '20px',
        height: visibleLines * 20 + 20, //we need to enforce the box's height so text doesnt peak out below the line clamped ellipsis line (lines * lineHeight + padding)
        m: 0,
        px: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: visibleLines,
        ...sx,
      }}
    />
  )
}
